import { ChatCircleDotsIcon, CircleNotchIcon } from '@phosphor-icons/react';

import type { ToolCallState } from '../../stores/agent-session-store';
import {
  type AskQuestion,
  parseAskAnswers,
  parseAskQuestions,
  parseAskReply,
  summarizeAnswer,
} from '../../stores/ask-user';

/**
 * The durable record of a question the agent asked, rendered in the transcript.
 *
 * Three states off one tool call, distinguished by whether it has a result and whether the run
 * that asked is still the current one. The pending state is deliberately *not* the form: the live
 * card is already pinned above the composer, and two copies of the same question is the worst
 * outcome available.
 */
export function AskUserToolCard({ toolCall, live }: { toolCall: ToolCallState; live: boolean }) {
  const questions = parseAskQuestions(toolCall.input);
  if (!questions) return null;
  const answers = parseAskAnswers(toolCall.output);

  if (!answers) {
    return live ? <WaitingRow /> : <AbandonedRow questions={questions} />;
  }

  const reply = parseAskReply(toolCall.output);
  if (reply) return <RepliedRow questions={questions} reply={reply} />;

  return (
    <div className="flex flex-col gap-2.5 rounded-lg border border-line-subtle bg-surface-1 px-3 py-2.5">
      <Eyebrow label={askedLabel(questions)} />
      {questions.map((question, i) => (
        <div key={`${question.header}-${i}`} className="flex flex-col gap-1">
          <p className="text-xs leading-snug text-fg-secondary">{question.question}</p>
          <p className="text-xs font-medium text-fg">
            {answers[i] && (answers[i]!.selected.length > 0 || answers[i]!.text) ? (
              summarizeAnswer(answers[i])
            ) : (
              <span className="font-normal text-fg-tertiary">The agent decided</span>
            )}
          </p>
        </div>
      ))}
    </div>
  );
}

/** The user answered every question at once, in their own words, so the reply is shown once. */
function RepliedRow({ questions, reply }: { questions: AskQuestion[]; reply: string }) {
  return (
    <div className="flex flex-col gap-1.5 rounded-lg border border-line-subtle bg-surface-1 px-3 py-2.5">
      <Eyebrow label={askedLabel(questions)} />
      {questions.map((question, i) => (
        <p key={`${question.header}-${i}`} className="text-xs leading-snug text-fg-secondary">
          {question.question}
        </p>
      ))}
      <p className="text-xs font-medium whitespace-pre-wrap text-fg">{reply}</p>
    </div>
  );
}

function askedLabel(questions: AskQuestion[]): string {
  return questions.length > 1 ? `Asked you ${questions.length} questions` : 'Asked you';
}

function Eyebrow({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-1.5 text-2xs font-medium text-fg-tertiary">
      <ChatCircleDotsIcon size={13} weight="fill" className="text-warning" />
      <span>{label}</span>
    </div>
  );
}

function WaitingRow() {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-line-subtle bg-surface-1 px-3 py-2">
      <CircleNotchIcon size={13} className="shrink-0 animate-spin text-fg-tertiary" />
      <ChatCircleDotsIcon size={14} weight="fill" className="shrink-0 text-warning" />
      <span className="text-xs font-medium text-fg">Waiting on your answer</span>
      <button
        type="button"
        onClick={() =>
          document.querySelector<HTMLElement>('[data-ask-user-card]')?.scrollIntoView({ block: 'nearest' })
        }
        className="ml-auto shrink-0 text-2xs text-fg-tertiary underline-offset-2 hover:text-fg-secondary hover:underline"
      >
        Jump to it
      </button>
    </div>
  );
}

/**
 * A question that was never answered and can no longer be — the conversation moved past it, so
 * the server would refuse the answer. Shown rather than hidden: it is why the agent chose what it
 * chose next.
 */
function AbandonedRow({ questions }: { questions: AskQuestion[] }) {
  return (
    <div className="flex flex-col gap-1.5 rounded-lg border border-line-subtle bg-surface-1 px-3 py-2.5">
      <Eyebrow label="Asked you" />
      {questions.map((question, i) => (
        <p key={`${question.header}-${i}`} className="text-xs leading-snug text-fg-tertiary">
          {question.question}
        </p>
      ))}
      <p className="text-2xs text-warning">Unanswered — the conversation moved on.</p>
    </div>
  );
}
